// components/GenerateMeetLink.js
import React, { useState, useEffect, useCallback } from 'react';
import { FaCopy, FaTrash, FaUser, FaCalendarAlt } from 'react-icons/fa';
import { gapi } from 'gapi-script';
import {
  useGetMyMeetingsQuery,
  useCreateMeetingMutation,
  useDeleteMeetingMutation,
} from '../slices/meetingSlice';
import { useSelector } from 'react-redux';
import 'bootstrap/dist/css/bootstrap.min.css';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;
const SCOPES = 'https://www.googleapis.com/auth/calendar.events';

const GenerateMeetLink = () => {
  const [meetLink, setMeetLink] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [startTime, setStartTime] = useState('12:00');
  const [endTime, setEndTime] = useState('13:00');
  const [eventName, setEventName] = useState('');
  const [googleApiReady, setGoogleApiReady] = useState(false);

  // Récupérer les informations de l'utilisateur connecté
  const { userInfo } = useSelector((state) => state.auth);

  // Redux RTK Query hooks
  const {
    data: meetingsData,
    isLoading: meetingsLoading,
    error: meetingsError,
    refetch,
  } = useGetMyMeetingsQuery(userInfo?._id);

  const [
    createMeeting,
    { error: createMeetingError, isLoading: createMeetingLoading },
  ] = useCreateMeetingMutation();
  const [deleteMeeting, { isLoading: deleteMeetingLoading }] =
    useDeleteMeetingMutation();

  const meetings = meetingsData?.data || [];

  // Afficher les erreurs
  useEffect(() => {
    if (meetingsError) {
      console.error('Erreur chargement meetings:', meetingsError);
    }

    if (createMeetingError) {
      console.error('Erreur création meeting:', createMeetingError);
      alert(
        `Erreur lors de la sauvegarde: ${createMeetingError.data?.message || 'Erreur serveur'}`,
      );
    }
  }, [meetingsError, createMeetingError]);

  const handleClientLoad = useCallback(() => {
    gapi.load('client:auth2', initClient);
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.onload = handleClientLoad;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [handleClientLoad]);

  const initClient = () => {
    gapi.client
      .init({
        apiKey: API_KEY,
        clientId: CLIENT_ID,
        discoveryDocs: [
          'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest',
        ],
        scope: SCOPES,
      })
      .then(() => {
        setGoogleApiReady(true);
        //console.log('✅ Google API initialisée');

        if (!gapi.auth2.getAuthInstance().isSignedIn.get()) {
          gapi.auth2.getAuthInstance().signIn();
        }
      })
      .catch((error) => {
        console.error('❌ Erreur initialisation Google API:', error);
        alert(
          'Failed to initialize Google API. Please check your credentials and try again.',
        );
      });
  };

  // Vérifier si l'heure de fin est après l'heure de début
  const validateTimes = () => {
    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    if (
      endHours < startHours ||
      (endHours === startHours && endMinutes <= startMinutes)
    ) {
      return false;
    }
    return true;
  };

  const createGoogleEvent = async () => {
    if (!validateTimes()) {
      alert("L'heure de fin doit être après l'heure de début");
      return;
    }

    setIsLoading(true);

    try {
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);

      const startDate = new Date(selectedDate);
      startDate.setHours(startHours, startMinutes, 0, 0);

      const endDate = new Date(selectedDate);
      endDate.setHours(endHours, endMinutes, 0, 0);

      const eventSummary = eventName.trim() || 'Google Meet Event';

      //console.log('🔄 Création événement Google Calendar...');

      const event = {
        summary: eventSummary,
        description: 'Generated Google Meet link using Google Calendar API',
        start: {
          dateTime: startDate.toISOString(),
          timeZone: 'Europe/Berlin',
        },
        end: {
          dateTime: endDate.toISOString(),
          timeZone: 'Europe/Berlin',
        },
        conferenceData: {
          createRequest: {
            requestId: Math.random().toString(36).substring(2, 15),
            conferenceSolutionKey: { type: 'hangoutsMeet' },
          },
        },
      };

      const response = await gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: event,
        conferenceDataVersion: 1,
      });

      const newMeetLink = response.result.hangoutLink;
      setMeetLink(newMeetLink);

      //console.log('✅ Lien Google Meet généré:', newMeetLink);

      // Sauvegarder dans MongoDB via Redux RTK Query
      const meetingData = {
        name: eventSummary,
        link: newMeetLink,
        date: selectedDate.toLocaleDateString('fr-FR'),
        startTime: startTime,
        endTime: endTime,
        datetime: startDate.toISOString(),
        endDatetime: endDate.toISOString(),
        createdBy: userInfo?.name || userInfo?.email || 'Formateur',
      };

      // console.log('💾 Sauvegarde dans MongoDB...', meetingData);

      await createMeeting(meetingData).unwrap(); // ✅ Suppression de la variable
      // console.log('✅ Meeting sauvegardé dans MongoDB:', result.data._id);

      // Recharger la liste des meetings
      refetch();

      setIsLoading(false);
      setEventName('');

      alert('Meeting créé avec succès et sauvegardé!');
    } catch (error) {
      console.error('❌ Erreur création événement:', error);
      setIsLoading(false);

      if (error.result && error.result.error) {
        alert(`Erreur Google API: ${error.result.error.message}`);
      } else {
        alert('Failed to create meeting. Please try again.');
      }
    }
  };

  const handleGenerateLink = () => {
    if (!googleApiReady) {
      alert('Google API not ready yet. Please wait...');
      return;
    }

    if (!validateTimes()) {
      alert("L'heure de fin doit être après l'heure de début");
      return;
    }

    if (gapi.auth2 && gapi.auth2.getAuthInstance()?.isSignedIn.get()) {
      createGoogleEvent();
    } else {
      gapi.auth2
        ?.getAuthInstance()
        ?.signIn()
        .then(createGoogleEvent)
        .catch((error) => {
          console.error('Error signing in:', error);
          alert('Failed to sign in to Google. Please try again.');
        });
    }
  };

  const handleDeleteMeeting = async (id, meetingName) => {
    if (
      window.confirm(
        `Êtes-vous sûr de vouloir supprimer le meeting "${meetingName}" ?`,
      )
    ) {
      try {
        await deleteMeeting(id).unwrap();
        refetch();
        alert('Meeting supprimé avec succès!');
      } catch (error) {
        console.error('Error deleting meeting:', error);
        alert('Erreur lors de la suppression du meeting');
      }
    }
  };

  const copyToClipboard = (link) => {
    navigator.clipboard.writeText(link);
    alert('Link copied to clipboard!');
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    setShowTimePicker(true);
  };

  const handleStartTimeChange = (e) => {
    setStartTime(e.target.value);
  };

  const handleEndTimeChange = (e) => {
    setEndTime(e.target.value);
  };

  const handleEventNameChange = (e) => {
    setEventName(e.target.value);
  };

  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute
          .toString()
          .padStart(2, '0')}`;
        times.push(
          <option key={timeString} value={timeString}>
            {timeString}
          </option>,
        );
      }
    }
    return times;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const calculateDuration = () => {
    if (!validateTimes()) return 'Durée invalide';

    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    const startTotalMinutes = startHours * 60 + startMinutes;
    const endTotalMinutes = endHours * 60 + endMinutes;
    const durationMinutes = endTotalMinutes - startTotalMinutes;

    const hours = Math.floor(durationMinutes / 60);
    const minutes = durationMinutes % 60;

    if (hours > 0) {
      return `${hours}h${minutes > 0 ? `${minutes}min` : ''}`;
    } else {
      return `${minutes}min`;
    }
  };

  return (
    <div className='container mt-5'>
      {/* En-tête avec informations utilisateur */}
      <div className='row mb-4'>
        <div className='col-12'>
          <div className='card bg-light'>
            <div className='card-body py-3'>
              <div className='row align-items-center'>
                <div className='col-md-6'>
                  <h5 className='card-title mb-0'>
                    <FaUser className='me-2 text-primary' />
                    Espace Formateur - Meetings
                  </h5>
                  <small className='text-muted'>
                    Connecté en tant que:{' '}
                    <strong>{userInfo?.name || userInfo?.email}</strong>
                  </small>
                </div>
                <div className='col-md-6 text-end'>
                  <span className='badge bg-info fs-6'>
                    {meetings.length} meeting{meetings.length !== 1 ? 's' : ''}{' '}
                    programmé{meetings.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Statut API */}
      <div className='row mb-3'>
        <div className='col-12'>
          <div
            className={`alert ${googleApiReady ? 'alert-success' : 'alert-warning'} d-flex align-items-center`}
          >
            <FaCalendarAlt className='me-2' />
            Status Google API: {googleApiReady ? '✅ Ready' : '🔄 Loading...'}
            {!googleApiReady && (
              <div
                className='spinner-border spinner-border-sm ms-2'
                role='status'
              >
                <span className='visually-hidden'>Loading...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Formulaire de génération */}
      <div
        className='card shadow-sm p-4 mb-5'
        style={{ maxWidth: '700px', margin: '0 auto' }}
      >
        <h3 className='mb-4 text-center text-primary'>
          <FaCalendarAlt className='me-2' />
          Créer un nouveau Meeting
        </h3>

        {(!CLIENT_ID || !API_KEY) && (
          <div className='alert alert-danger'>
            ⚠️ Google API credentials manquantes. Vérifiez vos variables
            d'environnement.
          </div>
        )}

        <div className='mb-3'>
          <label className='form-label fw-bold'>Nom de l'événement</label>
          <input
            type='text'
            className='form-control'
            placeholder="Entrez le nom de l'événement (optionnel)"
            value={eventName}
            onChange={handleEventNameChange}
            maxLength={100}
          />
          <div className='form-text'>{eventName.length}/100 caractères</div>
        </div>

        <div className='mb-3'>
          <label className='form-label fw-bold'>Sélectionner la date</label>
          <Calendar
            onChange={handleDateChange}
            value={selectedDate}
            minDate={new Date()}
            className='meet-calendar w-100'
          />
        </div>

        {showTimePicker && (
          <>
            <div className='row'>
              <div className='col-md-6 mb-3'>
                <label className='form-label fw-bold'>Heure de début</label>
                <select
                  className='form-select'
                  value={startTime}
                  onChange={handleStartTimeChange}
                >
                  {generateTimeOptions()}
                </select>
              </div>

              <div className='col-md-6 mb-3'>
                <label className='form-label fw-bold'>Heure de fin</label>
                <select
                  className='form-select'
                  value={endTime}
                  onChange={handleEndTimeChange}
                >
                  {generateTimeOptions()}
                </select>
              </div>
            </div>

            <div className='mb-3'>
              <div
                className={`alert ${validateTimes() ? 'alert-info' : 'alert-warning'}`}
              >
                <div className='d-flex justify-content-between align-items-center'>
                  <div>
                    <strong>Durée du meeting:</strong> {calculateDuration()}
                  </div>
                  <div>
                    <strong>Horaire:</strong> {startTime} - {endTime}
                    {!validateTimes() && (
                      <span className='text-danger ms-2'>
                        ⚠️ L'heure de fin doit être après le début
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        <button
          onClick={handleGenerateLink}
          className='btn btn-primary btn-lg mb-3'
          disabled={
            isLoading ||
            !API_KEY ||
            !CLIENT_ID ||
            !showTimePicker ||
            !googleApiReady ||
            createMeetingLoading ||
            !validateTimes()
          }
          style={{ width: '100%' }}
        >
          {isLoading || createMeetingLoading ? (
            <>
              <span
                className='spinner-border spinner-border-sm me-2'
                role='status'
              ></span>
              {createMeetingLoading
                ? 'Sauvegarde en cours...'
                : 'Génération en cours...'}
            </>
          ) : (
            <>
              <FaCalendarAlt className='me-2' />
              Générer le Lien Meeting
            </>
          )}
        </button>

        {meetLink && (
          <div className='alert alert-success'>
            <strong>✅ Meeting Google créé avec succès!</strong>
            <div className='input-group mt-2'>
              <input
                type='text'
                className='form-control'
                value={meetLink}
                readOnly
              />
              <button
                onClick={() => copyToClipboard(meetLink)}
                className='btn btn-outline-secondary'
                type='button'
              >
                <FaCopy className='me-2' />
                Copier
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tableau des meetings du formateur */}
      <div className='card shadow-sm'>
        <div className='card-header bg-primary text-white'>
          <h4 className='mb-0'>
            <FaCalendarAlt className='me-2' />
            Mes Meetings Programmes
          </h4>
        </div>

        <div className='card-body'>
          {meetingsLoading && (
            <div className='text-center py-4'>
              <div className='spinner-border text-primary' role='status'>
                <span className='visually-hidden'>Loading...</span>
              </div>
              <p className='mt-2'>Chargement de vos meetings...</p>
            </div>
          )}

          {meetingsError && (
            <div className='alert alert-danger text-center'>
              Erreur lors du chargement de vos meetings
            </div>
          )}

          {!meetingsLoading && meetings.length === 0 && (
            <div className='text-center text-muted py-5'>
              <FaCalendarAlt size={48} className='mb-3 opacity-50' />
              <h5>Aucun meeting programmé</h5>
              <p>
                Utilisez le formulaire ci-dessus pour créer votre premier
                meeting.
              </p>
            </div>
          )}

          {meetings.length > 0 && (
            <>
              <div className='table-responsive'>
                <table className='table table-striped table-hover'>
                  <thead className='table-dark'>
                    <tr>
                      <th scope='col'>Nom de l'événement</th>
                      <th scope='col'>Date</th>
                      <th scope='col'>Heure Début</th>
                      <th scope='col'>Heure Fin</th>
                      <th scope='col'>Lien Meeting</th>
                      <th scope='col'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {meetings.map((meeting) => (
                      <tr key={meeting._id}>
                        <td>
                          <strong>{meeting.name}</strong>
                        </td>
                        <td>
                          <strong>{formatDate(meeting.datetime)}</strong>
                        </td>
                        <td>
                          <span className='badge bg-primary fs-6'>
                            {meeting.startTime}
                          </span>
                        </td>
                        <td>
                          <span className='badge bg-secondary fs-6'>
                            {meeting.endTime}
                          </span>
                        </td>
                        <td>
                          <div className='input-group input-group-sm'>
                            <input
                              type='text'
                              className='form-control form-control-sm'
                              value={meeting.link}
                              readOnly
                              style={{ fontSize: '0.8rem' }}
                            />
                            <button
                              onClick={() => copyToClipboard(meeting.link)}
                              className='btn btn-outline-secondary btn-sm'
                              type='button'
                              title='Copier le lien'
                            >
                              <FaCopy />
                            </button>
                          </div>
                        </td>
                        <td>
                          <button
                            onClick={() =>
                              handleDeleteMeeting(meeting._id, meeting.name)
                            }
                            className='btn btn-outline-danger btn-sm'
                            title='Supprimer le meeting'
                            disabled={deleteMeetingLoading}
                          >
                            <FaTrash />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className='mt-3 text-muted'>
                <small>Total de vos meetings: {meetings.length}</small>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenerateMeetLink;
